import { useState } from "react";
import { Search, ChevronDown } from "lucide-react";
import CTASection from "../components/CTASection";

const CATEGORIES = [
  {
    title: "Getting started",
    items: [
      { q: "How do I sign up for Brivo?", a: "Click 'Get Started' on any page, enter your email, and you're in. The whole process takes under 60 seconds. No credit card required for the free trial." },
      { q: "Is there a free plan?", a: "Yes — Brivo Starter is free forever for solo founders, with up to 100 products and one staff account. Upgrade only when you outgrow it." },
      { q: "Can I import data from Shopify or WooCommerce?", a: "Absolutely. Brivo includes one-click migration tools for Shopify, WooCommerce, QuickBooks, and CSV. White-glove migration is free on Business plans and above." },
      { q: "How long does setup take?", a: "Most businesses are fully onboarded in under 2 hours. Enterprise customers receive a dedicated implementation specialist." },
    ],
  },
  {
    title: "Inventory & POS",
    items: [
      { q: "Can I manage multiple warehouses?", a: "Yes — Brivo supports unlimited warehouses, with one-click transfers between locations and per-warehouse stock levels." },
      { q: "Do I need a barcode scanner?", a: "No. Use your phone camera as a scanner. If you prefer hardware, Brivo works with any USB or Bluetooth barcode scanner." },
      { q: "Does the POS work offline?", a: "Yes. The Brivo POS works fully offline and syncs back when you're online. You'll never miss a sale." },
      { q: "Can I bundle products together?", a: "Yes — kits, bundles, and BOMs (bill of materials) are supported natively." },
    ],
  },
  {
    title: "Payments & Billing",
    items: [
      { q: "What payment methods do you support?", a: "All major cards, bank transfers, USSD, mobile money, Apple Pay, Google Pay — across 30+ countries and 150+ currencies." },
      { q: "How fast are payouts?", a: "Same-day for most regions, T+1 for international. Enterprise customers can configure custom payout schedules." },
      { q: "Are there transaction fees?", a: "No platform fees. You only pay your processor's standard rate. What you charge your customer is what you keep (minus processor fees)." },
      { q: "Can I issue refunds?", a: "Yes — full or partial refunds in one click, with audit trail and automatic stock restoration." },
    ],
  },
  {
    title: "Account & Security",
    items: [
      { q: "Is my data secure?", a: "Bank-grade encryption (AES-256), SOC 2 Type II certified, ISO 27001 certified, and GDPR-compliant. Your data is never sold or shared." },
      { q: "Do you support SSO?", a: "Yes — SAML 2.0 SSO is available on Business plans and above, with Google Workspace, Okta, Azure AD, and OneLogin support." },
      { q: "Can I export my data?", a: "Anytime, in any format (CSV, JSON, Excel). It's your data — we make it easy to take with you." },
      { q: "What happens if I cancel?", a: "Your data is retained for 90 days after cancellation. You can re-activate anytime, or fully export and delete on request." },
    ],
  },
];

export default function FAQs() {
  const [search, setSearch] = useState("");

  const filtered = CATEGORIES.map((c) => ({
    ...c,
    items: c.items.filter(
      (it) => it.q.toLowerCase().includes(search.toLowerCase()) || it.a.toLowerCase().includes(search.toLowerCase())
    ),
  })).filter((c) => c.items.length > 0);

  return (
    <div data-testid="faqs-page">
      <section className="hero-gradient relative overflow-hidden grain">
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 py-24 md:py-28 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full glass-strong border border-[#12b571]/10 text-black text-xs font-semibold mb-6">FAQs</span>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold text-black tracking-tight leading-[1.05]">
            Common questions.<br />
            <span className="italic text-black/60 font-medium">Honest answers.</span>
          </h1>
          <p className="mt-7 text-lg text-black/70 max-w-xl mx-auto leading-relaxed">
            Can't find what you're looking for? Reach out — our team typically replies within 2 hours.
          </p>

          <div className="mt-10 max-w-xl mx-auto relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-black/40" size={20} />
            <input
              data-testid="faq-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search FAQs..."
              className="w-full pl-14 pr-5 py-4 rounded-2xl border border-[#12b571]/15 bg-white shadow-sm focus:border-[#12b571] focus:outline-none transition-colors"
            />
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 space-y-12">
          {filtered.map((cat) => (
            <div key={cat.title}>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold text-black mb-6">{cat.title}</h2>
              <div className="space-y-3">
                {cat.items.map((it, i) => (
                  <details key={i} className="bg-[#FFFCF2] rounded-2xl p-5 group border border-[#12b571]/8">
                    <summary className="font-semibold text-black cursor-pointer flex justify-between items-center">
                      {it.q}
                      <ChevronDown size={18} className="group-open:rotate-180 transition-transform flex-shrink-0 ml-4" />
                    </summary>
                    <p className="mt-3 text-black/75 leading-relaxed">{it.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-center text-black/60">No questions match "{search}". Try a different search.</p>
          )}
        </div>
      </section>

      <CTASection accent="pink" title="Still have questions?" subtitle="Our team is one click away — and we're genuinely happy to help." />
    </div>
  );
}
